/* Popup modal for Dawn — multi-instance, robust close + single-open enforcement */
class PopupModal extends HTMLElement {
  static registry = new Map();
  static _globalBound = false;

  static bindGlobalOpenerListener() {
    if (PopupModal._globalBound) return;
    PopupModal._globalBound = true;

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-popup-open]');
      if (!opener) return;

      const id = opener.getAttribute('data-popup-open');
      if (!id) return;

      const modal = PopupModal.registry.get(id);
      if (!modal) return;

      e.preventDefault();
      // Close any other open modals before opening this one
      PopupModal.registry.forEach((m) => { if (m !== modal) m.hide(); });
      modal.show();
    });
  }

  constructor() {
    super();
    this.dialog = null;
    this.sectionId = '';

    // Bound handlers
    this.onEsc = (e) => { if (e.key === 'Escape' && this.isOpen()) this.hide(); };
    this.onBackdrop = (e) => { if (e.target === this.dialog) this.hide(); };
    this.onInsideClick = (e) => {
      // Event delegation for ANY [data-popup-close] inside the modal
      if (e.target.closest('[data-popup-close]')) {
        e.preventDefault();
        this.hide();
      }
    };
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || this.id || '';
    if (!this.sectionId) {
      console.warn('popup-modal: Missing data-section-id or id on', this);
      return;
    }

    // Resolve dialog now; also re-resolve lazily in show()
    this.dialog = this.querySelector('dialog');

    // Register and bind global opener router
    PopupModal.registry.set(this.sectionId, this);
    PopupModal.bindGlobalOpenerListener();

    // Instance listeners
    if (this.dialog) {
      this.dialog.addEventListener('click', this.onBackdrop);
      this.addEventListener('click', this.onInsideClick); // delegate close buttons
    }
  }

  disconnectedCallback() {
    PopupModal.registry.delete(this.sectionId);
    document.removeEventListener('keydown', this.onEsc);
    if (this.dialog) this.dialog.removeEventListener('click', this.onBackdrop);
    this.removeEventListener('click', this.onInsideClick);
  }

  isOpen() {
    return !!(this.dialog && (this.dialog.open || this.dialog.hasAttribute('open')));
  }

  show() {
    // Lazy resolve in case content was injected late
    if (!this.dialog) {
      this.dialog = this.querySelector('dialog');
      if (this.dialog) {
        this.dialog.addEventListener('click', this.onBackdrop);
        this.addEventListener('click', this.onInsideClick);
      }
    }
    if (!this.dialog) { console.warn('popup-modal: <dialog> not found'); return; }
    if (this.isOpen()) return;

    if (this.dialog.showModal) this.dialog.showModal();
    else this.dialog.setAttribute('open', '');

    document.documentElement.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onEsc);

    this.dispatchEvent(new CustomEvent('popup:open', { bubbles: true }));
  }

  hide() {
    if (!this.dialog) return;
    if (!this.isOpen()) return;

    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');

    document.documentElement.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onEsc);

    this.dispatchEvent(new CustomEvent('popup:close', { bubbles: true }));
  }
}

customElements.define('popup-modal', PopupModal);
